import React from 'react';
import { SxProps } from '@mui/system';
import { Box, Button } from '@mui/material';

export interface BinaryToggleOption<T> {
  value: T;
  label: string;
}

export interface BinaryToggleProps<T> {
  value: T;
  onChange: (newValue: T) => void;
  options: [BinaryToggleOption<T>, BinaryToggleOption<T>];
  disabled?: boolean;
  sx?: SxProps;
  buttonWidth?: string | number;
}

export const BinaryToggle = <T,>({
  value,
  onChange,
  options,
  disabled = false,
  sx,
  buttonWidth = '120px',
}: BinaryToggleProps<T>) => {
  const buttonStyle: SxProps = {
    textTransform: 'none',
    fontWeight: 'semibold',
    fontSize: '12px',
    borderRadius: '8px',
    color: 'grey.600',
    height: '32px',
    width: buttonWidth,
    '&:hover': {
      backgroundColor: 'grey.300',
    },
  };

  const enabledStyle: SxProps = {
    backgroundColor: 'primary.dark',
    color: 'white',
    '&:hover': {
      backgroundColor: 'grey.700',
    },
    '&:disabled': {
      color: 'grey.200',
      backgroundColor: 'grey.700',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        padding: 0.5,
        borderRadius: '8px',
        backgroundColor: 'grey.100',
        gap: 0.5,
        width: 'fit-content',
        ...sx,
      }}
    >
      {options.map((option, index) => (
        <Button
          key={index}
          variant="text"
          sx={{
            ...buttonStyle,
            ...(value === option.value ? enabledStyle : {}),
          }}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
};
